// Bannière de consentement Space Syndicate (CONF-14, snippet fs-consent) sur l'API de confidentialité de Shopify.
// Shopify reste le moteur : il dit si la région exige une bannière (shouldShowBanner), garde le choix
// (currentVisitorConsent) et l'enregistre (setTrackingConsent). Ce module n'affiche rien tant que l'API n'est pas chargée ; sans elle, la bannière de Shopify
// reste en place. Un choix n'est jamais enregistré sans geste du visiteur.

import { t } from './fs-i18n.js';

const PURPOSES = ['analytics', 'marketing', 'preferences'];

/** Charge l'API (consent-tracking-api 0.1) ; null si Shopify ne la fournit pas, ou incomplète (labo, script bloqué). */
import { bindDialog } from './fs-ui-dialog.js';

export function loadPrivacyApi(win = window, timeoutMs = 5000) {
  return new Promise((resolve) => {
    const shopify = win.Shopify;
    const usable = api => typeof api?.setTrackingConsent === 'function' && typeof api?.shouldShowBanner === 'function';
    if (usable(shopify?.customerPrivacy)) return resolve(shopify.customerPrivacy);
    if (typeof shopify?.loadFeatures !== 'function') return resolve(null);
    const timer = win.setTimeout(() => resolve(null), timeoutMs);
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        win.clearTimeout(timer);
        const api = win.Shopify?.customerPrivacy;
        resolve(!error && usable(api) ? api : null);
      });
    } catch {
      win.clearTimeout(timer);
      resolve(null);
    }
  });
}

/** Choix envoyé à Shopify : tout, rien, ou les cases cochées. Comme la bannière de Shopify, jamais la vente de données
    (sale_of_data), qui a sa propre page de refus : un refus déjà donné n'est jamais écrasé. */
export function consentPayload(choice, checked = {}) {
  const value = purpose => (choice === 'all' ? true : choice === 'none' ? false : checked[purpose] === true);
  return Object.fromEntries(PURPOSES.map(purpose => [purpose, value(purpose)]));
}

/**
 * Bannière à montrer ? shouldShowBanner() ne dit que « la région exige une bannière » (indicateur display_banner de
 * Shopify, toujours vrai après un choix : constaté en ligne le 9 octobre 2026, comme le fait la bannière de Shopify) ;
 * il faut en plus qu'aucun choix ne soit enregistré (« yes » ou « no » pour une finalité). null : réponse indéterminée.
 */
export function bannerNeeded(region, current) {
  if (typeof region !== 'boolean') return null;
  const decided = PURPOSES.some(purpose => current?.[purpose] === 'yes' || current?.[purpose] === 'no');
  return region && !decided;
}

/** Cases du détail d'après le choix déjà enregistré (« yes » seulement ; rien de coché par défaut). */
export function checkedFromConsent(current) {
  return Object.fromEntries(PURPOSES.map(purpose => [purpose, current?.[purpose] === 'yes']));
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Premier élément atteignable au clavier après `node` dans le document, hors de lui. */
function nextFocusable(node) {
  for (const el of node.ownerDocument.querySelectorAll(FOCUSABLE)) {
    if (node.contains(el) || !(node.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
    if (el.closest('[hidden], [inert]') || !el.getClientRects().length) continue;
    return el;
  }
  return null;
}

function init(root, api) {
  const doc = root.ownerDocument;
  // Réponse indéterminée de Shopify (exception, autre chose qu'un booléen) : on ne prend pas le relais, la bannière de
  // Shopify reste. Jamais les deux masquées pour un visiteur qui doit choisir.
  let region;
  let current = null;
  try { region = api.shouldShowBanner(); } catch { region = undefined; }
  try { current = api.currentVisitorConsent?.() ?? null; } catch { current = null; }
  const show = bannerNeeded(region, current);
  if (show === null) return false;
  const banner = root.querySelector('[data-fs-consent-banner]');
  const dialog = root.querySelector('[data-fs-consent-prefs]');
  const form = root.querySelector('[data-fs-consent-form]');
  const status = root.querySelector('[data-fs-consent-status]');
  const errors = [...root.querySelectorAll('[data-fs-consent-error]')];
  const boxes = [...root.querySelectorAll('[data-fs-consent-purpose]')];
  let opener = null;
  let busy = false;

  const announce = (text) => { status.textContent = ''; window.setTimeout(() => { status.textContent = text; }, 50); };
  // Dialogue du catalogue (fs-ui-dialog.js) : Échap en un appui, Tab bouclé, clic sur le fond. Pendant un enregistrement,
  // il ne se ferme pas (ni Échap, ni ×, ni le fond) : une erreur doit rester visible. Focus rendu ci-dessous.
  const prefs = bindDialog(dialog, { canClose: () => !busy, restoreFocus: false });
  const closeDialog = (force = false) => { if (dialog.open && (force || !busy)) dialog.close(); };
  const openDialog = (from) => {
    opener = from ?? doc.activeElement;
    let current = null;
    try { current = api.currentVisitorConsent?.(); } catch { current = null; }
    const checked = checkedFromConsent(current);
    for (const box of boxes) box.checked = checked[box.dataset.fsConsentPurpose] === true;
    prefs.open({ opener: opener ?? null });
  };
  dialog.addEventListener('close', () => {
    const target = opener?.isConnected && !opener.closest('[hidden]') ? opener : doc.querySelector('[data-fs-consent-manage]');
    opener = null;
    target?.focus?.({ preventScroll: true });
  });

  const save = (choice, trigger) => {
    if (busy) return;
    busy = true;
    form.setAttribute('aria-busy', 'true');
    const checked = Object.fromEntries(boxes.map(box => [box.dataset.fsConsentPurpose, box.checked]));
    const done = (error) => {
      busy = false;
      form.removeAttribute('aria-busy');
      for (const box of errors) { box.hidden = true; box.textContent = ''; }
      if (error) {
        // Message visible là où le visiteur se trouve : dans le dialogue s'il est ouvert (le reste de la page est
        // alors inerte), sinon dans la bannière ; role="alert" l'annonce.
        const place = dialog.open ? dialog : banner.hidden ? null : banner;
        const where = place && errors.find(box => place.contains(box));
        if (where) { where.hidden = false; where.textContent = t('consent.failed'); } else announce(t('consent.failed'));
        return;
      }
      const wasInBanner = banner.contains(trigger);
      banner.hidden = true;
      closeDialog(true);
      announce(t('consent.saved'));
      // Le focus était dans la bannière qui disparaît : il passe à l'élément suivant dans l'ordre de lecture (l'en-tête).
      if (wasInBanner) nextFocusable(root)?.focus?.({ preventScroll: true });
    };
    try {
      api.setTrackingConsent(consentPayload(choice, checked), (error) => done(error ?? null));
    } catch (error) {
      done(error);
    }
  };

  root.addEventListener('click', (event) => {
    const choice = event.target.closest('[data-fs-consent-choice]');
    if (choice && choice.dataset.fsConsentChoice !== 'form') { event.preventDefault(); save(choice.dataset.fsConsentChoice, choice); return; }
    if (event.target.closest('[data-fs-consent-open]')) { openDialog(event.target.closest('[data-fs-consent-open]')); return; }
    if (event.target.closest('[data-fs-consent-close]')) closeDialog();
  });
  form.addEventListener('submit', (event) => { event.preventDefault(); save('form', event.submitter); });

  // « Gérer les cookies » (pied de page) : visible seulement quand l'API est là, jamais un bouton mort.
  for (const manage of doc.querySelectorAll('[data-fs-consent-manage]')) {
    manage.hidden = false;
    manage.addEventListener('click', () => openDialog(manage));
  }
  // Choix fait ailleurs (autre onglet, autre outil) : la bannière disparaît.
  doc.addEventListener('visitorConsentCollected', () => { banner.hidden = true; });

  doc.documentElement.dataset.fsConsent = 'custom';
  root.hidden = false;
  banner.hidden = !show;
  return true;
}

const root = typeof document !== 'undefined' ? document.querySelector('[data-fs-consent]') : null;
if (root) {
  // Pendant le chargement de l'API, la bannière de Shopify reste masquée (pas d'apparition éclair avant la nôtre) ;
  // si l'API ne vient pas, l'attribut est retiré et elle réapparaît : jamais aucune bannière.
  const html = document.documentElement;
  if (typeof window.Shopify?.loadFeatures === 'function') html.dataset.fsConsent = 'pending';
  loadPrivacyApi(window, 5000).then((api) => { if (!api || !init(root, api)) delete html.dataset.fsConsent; });
}
